import React, { lazy, useEffect, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useStore from "../../store";
import { PTMConfirmDialog, PTMAppBar } from "../../Components";

const importWidget = (component) => lazy(() => import("../../Widgets/" + component + "/" + component)); // eslint-disable-line
const importWidgetSettings = (component) => lazy(() => import("../../Widgets/" + component + "/" + component + "Settings")); // eslint-disable-line
const importPluginWidget = (plugin, component) => lazy(() => new Promise((resolve) => {
    import("../../Plugins/" + plugin + "/Widgets/" + component + "/" + component).then((componentPlugin) => { // eslint-disable-line
        resolve(componentPlugin);
    }).catch(() => {
        import("../../Widgets/ComponentNotFound/ComponentNotFound").then((notFound) => {
            resolve(notFound);
        });
    });
}));

const importPluginWidgetSettings = (plugin, component) => lazy(() => new Promise((resolve) => {
    import("../../Plugins/" + plugin + "/Widgets/" + component + "/" + component + "Settings").then((componentPlugin) => { // eslint-disable-line
        resolve(componentPlugin);
    }).catch(() => {
        import("../../Widgets/ComponentNotFound/ComponentNotFound").then((notFound) => {
            resolve(notFound);
        });
    });
}));

function Widget(props) {
    const { widget } = props;

    const [settingsView, setSettingsView] = useState([]);
    const [view, setView] = useState([]);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [openSettings, setOpenSettings] = useState(false);
    const [isHidden, setIsHidden] = useState(props.widget.isHidden);
    const isHelpDesk = useStore((store) => store.app.appInfo.is_helpdesk);
    const removeWidget = useStore((store) => store.dashboard.widgets.remove);

    const onRemoveWidget = () => {
        if (props.removeWidget) {
            props.removeWidget(props.section, widget.id);
        } else {
            removeWidget(props.section, widget.id);
        }
    };

    useEffect(() => {
        (async () => {
            if (widget.isCore) {
                const ImportedWidget = await importWidget(widget.component);

                setView(<ImportedWidget
                    widget={widget}
                    enabled={props.enabled}
                    // settings={widget.settings}
                    section={props.section}
                />);
            } else {
                const ImportedWidget = await importPluginWidget(
                    widget.plugin[0].toUpperCase() + widget.plugin.slice(1),
                    widget.component,
                );

                setView(<ImportedWidget
                    widget={widget}
                    enabled={props.enabled}
                    // settings={widget.settings}
                    section={props.section}
                />);
            }
        })();
    }, [widget.settings, props.enabled]);

    useEffect(() => {
        (async () => {
            if (widget.hasSettings) {
                if (widget.isCore) {
                    const WidgetSettings = await importWidgetSettings(widget.component);

                    setSettingsView(<WidgetSettings
                        widget={widget}
                        openSettings={openSettings}
                        closeSettings={() => setOpenSettings(false)}
                        section={props.section}
                    />);
                } else {
                    const WidgetSettings = await importPluginWidgetSettings(
                        widget.plugin[0].toUpperCase() + widget.plugin.slice(1),
                        widget.component,
                    );

                    setSettingsView(<WidgetSettings
                        widget={widget}
                        openSettings={openSettings}
                        closeSettings={() => setOpenSettings(false)}
                        section={props.section}
                    />);
                }
            }
        })();
    }, [widget, openSettings]);

    const showWidget = (widgetsId) => {
        setIsHidden(false);
        props.showWidget(widgetsId);
    };

    const hideWidget = (widgetsId) => {
        setIsHidden(true);
        props.hideWidget(widgetsId);
    };

    return (
        <>
            <PTMAppBar
                helpName={!isHelpDesk && !props.isEditor && `widget-${widget.component}`}
                icon={widget.dynamicIcon || widget.icon}
                title={(widget.dynamicTitle || widget.title) + (widget.titleAddon ? (` ${widget.titleAddon}`) : "")}
                clickableTitle={widget.clickableTitle}
                onTitleClick={widget.onTitleClick}
                titleTooltip={widget.titleTooltip}
                draggable={!isHelpDesk && (!props.isEditor || props.isEditing)}
                controlButtons={
                    [
                        {
                            hide: !widget.reloadHandler,
                            icon: "sync-alt",
                            title: "Neu laden",
                            color: "inherit",
                            click: widget.reloadHandler,
                        },
                        {
                            hide: !props.widget.hasSettings,
                            icon: "cog",
                            title: "Einstellungen",
                            color: "inherit",
                            click: () => setOpenSettings(true),
                        },
                        ...(!isHelpDesk && !props.isEditor) ? [
                            {
                                hide: !isHidden,
                                icon: "window-maximize",
                                title: "Aufklappen",
                                color: "inherit",
                                click: () => showWidget(widget.id),
                            },
                            {
                                hide: isHidden,
                                icon: "window-minimize",
                                title: "Zuklappen",
                                color: "inherit",
                                click: () => hideWidget(widget.id),
                            },
                        ] : [],
                        ...(!isHelpDesk && (!props.isEditor || props.isEditing)) ? [
                            {
                                icon: "window-close",
                                title: "Löschen",
                                color: "inherit",
                                click: () => setConfirmDelete(true),
                            },
                        ] : [],
                    ]
                }
            />
            <PTMConfirmDialog
                title="Widget löschen?"
                open={confirmDelete}
                setOpen={setConfirmDelete}
                onConfirm={onRemoveWidget}
            >
                Beim Löschen von Widgets gehen die Widgeteinstellungen verloren!
            </PTMConfirmDialog>
            <div
                style={{
                    height: "calc(100% - 25px)",
                    overflow: "auto",
                }}
            >
                <React.Suspense fallback={(
                    <FontAwesomeIcon
                        mt={6}
                        ml={2}
                        mr={2}
                        icon="spinner"
                        spin
                    />
                )}
                >
                    {React.useMemo(() => (
                        <div style={{
                            display: isHidden ? "none" : "initial",
                        }}
                        >
                            {view}
                        </div>
                    ), [view, isHidden])}
                </React.Suspense>
                <React.Suspense fallback={(
                    <FontAwesomeIcon
                        mt={6}
                        ml={2}
                        mr={2}
                        icon="spinner"
                        spin
                    />
                )}
                >
                    {React.useMemo(() => (
                        <div>
                            {openSettings && settingsView}
                        </div>
                    ), [openSettings, settingsView])}
                </React.Suspense>
            </div>
        </>
    );
}

export default React.memo(Widget);
